// src/pages/info/documentation.tsx
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Download, Copy, Check, FileText, Book, Eye, Clock, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";

interface DocItem {
  id: string;
  title: string;
  description: string;
  filename: string;
  bulletPointsFilename: string;
  size: string;
  readingTime: string; // Approximate reading time
  bulletPointsReadingTime: string;
  category: string;
}

const documents: DocItem[] = [
  {
    id: "ai-discovery-guide",
    title: "AI Discovery Guide",
    description:
      "Complete guide to llms.txt and agents.txt standards. Learn what they are, why they matter in 2025, how AI systems use them, and step-by-step implementation. Includes insights from analyzing 1M ChatGPT agent actions, best practices, and real-world examples from Anthropic, LangChain, and Cursor. Perfect for understanding the standard yourself or training your team.",
    filename: "ai-discovery-guide.md",
    bulletPointsFilename: "ai-discovery-guide-bullet-points.md",
    size: "~3,000 words",
    readingTime: "~12 min read",
    bulletPointsReadingTime: "~3 min read",
    category: "Learning",
  },
  {
    id: "white-label-template",
    title: "AI Discovery White-Label Template",
    description:
      "Professional proposal template for agencies selling AI discovery services to clients. Includes executive summary, ROI justification, 4-week implementation timeline, multiple pricing options, and research-backed insights. Fully customizable with placeholder variables for client and agency information. Ready to send to clients to win new business.",
    filename: "ai-discovery-white-label-template.md",
    bulletPointsFilename: "ai-discovery-white-label-template-bullet-points.md",
    size: "~7,000 words",
    readingTime: "~28 min read",
    bulletPointsReadingTime: "~3 min read",
    category: "Agency Tools",
  },
  {
    id: "customization-checklist",
    title: "Agency Customization Checklist",
    description:
      "Step-by-step implementation guide for agencies executing AI discovery projects. Complete checklist for customizing templates, pricing guidance by business size ($800-$15k+ setup), discovery questions, delivery workflow, and upsell opportunities. Includes success metrics to track and troubleshooting for common issues.",
    filename: "agency-customization-checklist.md",
    bulletPointsFilename: "agency-customization-checklist-bullet-points.md",
    size: "~4,500 words",
    readingTime: "~18 min read",
    bulletPointsReadingTime: "~2 min read",
    category: "Agency Tools",
  },
  {
    id: "generate-prompt",
    title: "DIY Generation Prompt",
    description:
      "Simple copy-paste prompt for small business owners to generate their own llms.txt and agents.txt files using ChatGPT or Claude. Includes three versions (30min comprehensive, 10min quick, 5min minimal), implementation instructions, troubleshooting, and success checklist. No technical knowledge required - just answer questions and get AI-ready files.",
    filename: "generate-ai-discovery-files-prompt.md",
    bulletPointsFilename: "generate-ai-discovery-files-prompt-bullet-points.md",
    size: "~3,000 words",
    readingTime: "~12 min read",
    bulletPointsReadingTime: "~1 min read",
    category: "DIY Tools",
  },
];

export default function DocumentationPage() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleDownload = async (filename: string, title: string) => {
    try {
      const response = await fetch(`/docs/${filename}`);
      if (!response.ok) throw new Error("Failed to fetch document");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Download started",
        description: `Downloading ${title}`,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyMarkdown = async (filename: string, id: string, title: string) => {
    try {
      const response = await fetch(`/docs/${filename}`);
      if (!response.ok) throw new Error("Failed to fetch document");

      const markdown = await response.text();
      await navigator.clipboard.writeText(markdown);

      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);

      toast({
        title: "Copied to clipboard",
        description: `${title} markdown copied successfully`,
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Could not copy markdown. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Book className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold">Documentation</h1>
        </div>
        <p className="text-lg text-muted-foreground">
          Comprehensive guides and templates for AI discovery optimization,
          llms.txt and agents.txt implementation, and agency services.
        </p>
      </div>

      {/* Introduction Card */}
      <Card className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            AI Discovery Documentation Series
          </CardTitle>
          <CardDescription className="text-base">
            Complete toolkit for making your business discoverable by AI
            assistants like ChatGPT, Claude, and Gemini
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm">
              <strong>What is AI discovery?</strong> AI discovery optimization
              helps AI assistants accurately understand and recommend your
              business when users ask questions. With 800M+ weekly ChatGPT users
              and growing AI-powered search, these files are becoming essential
              for business visibility.
            </p>
            <p className="text-sm">
              <strong>What's included:</strong> Four comprehensive documents
              covering learning materials, agency templates, implementation
              checklists, and DIY tools. Over 23,000 words of actionable content
              ready to use, share, or white-label.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                llms.txt Standard
              </span>
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                agents.txt Guide
              </span>
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                Agency Templates
              </span>
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                DIY Tools
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* All Documents in 2x2 Grid */}
      <div className="grid gap-6 md:grid-cols-2 mb-8">
        {documents.map((doc) => (
            <Card
              key={doc.id}
              className="flex flex-col"
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">{doc.title}</CardTitle>
                </div>
                <CardDescription className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                  <span>{doc.size}</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {doc.readingTime}
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-sm text-muted-foreground mb-4">
                  {doc.description}
                </p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      asChild
                      className={doc.id === "generate-prompt" ? "" : "flex-1"}
                    >
                      <Link to={`/documentation/${doc.id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        Read Document
                      </Link>
                    </Button>
                    {doc.id !== "generate-prompt" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        asChild
                        className="flex-1"
                      >
                        <Link to={`/documentation/${doc.id}/bullet-points`}>
                          <Zap className="h-4 w-4 mr-2" />
                          Bullet Points
                        </Link>
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc.filename, doc.title)}
                      className="flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleCopyMarkdown(doc.filename, doc.id, doc.title)
                      }
                      className="flex-1"
                    >
                      {copiedId === doc.id ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>

      {/* Usage Guide */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>How to Use These Documents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">For Learning & Implementation</h3>
            <p className="text-sm text-muted-foreground">
              Start with the <strong>AI Discovery Guide</strong> to understand
              the llms.txt and agents.txt standards. Then use the{" "}
              <strong>DIY Generation Prompt</strong> to create your own files, or
              implement manually following the guide's instructions.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">For Agencies & Consultants</h3>
            <p className="text-sm text-muted-foreground">
              Use the <strong>White-Label Template</strong> to create client
              proposals, and the <strong>Customization Checklist</strong> to
              execute projects. Customize pricing, add your branding, and deliver
              professional AI discovery services.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">For Small Business Owners</h3>
            <p className="text-sm text-muted-foreground">
              Jump straight to the <strong>DIY Generation Prompt</strong>. Copy
              it into ChatGPT or Claude, answer the questions, and get your
              llms.txt and agents.txt files generated automatically in 30 minutes
              or less.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">File Formats</h3>
            <p className="text-sm text-muted-foreground">
              All documents are in Markdown format (.md). You can open them in
              any text editor, import into Google Docs, or use tools like Typora
              or Mark Text for formatted viewing. Download to save locally, or
              copy markdown to paste into your own documents.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Additional Resources */}
      <Card className="mt-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200 dark:border-green-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-900 dark:text-green-100">
            <Book className="h-5 w-5 text-green-600 dark:text-green-400" />
            Additional Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li>
              <strong>Live Examples:</strong>{" "}
              <a
                href="/llms.txt"
                target="_blank"
                className="text-primary hover:underline"
              >
                8020agent.com/llms.txt
              </a>{" "}
              and{" "}
              <a
                href="/agents.txt"
                target="_blank"
                className="text-primary hover:underline"
              >
                8020agent.com/agents.txt
              </a>
            </li>
            <li>
              <strong>AI Context Page:</strong>{" "}
              <a
                href="/ai-context"
                target="_blank"
                className="text-primary hover:underline"
              >
                Human-readable AI discovery information
              </a>
            </li>
            <li>
              <strong>Standard Specification:</strong>{" "}
              <a
                href="https://llmstxt.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                llmstxt.org
              </a>
            </li>
            <li>
              <strong>Research:</strong>{" "}
              <a
                href="https://searchengineland.com/insights-chatgpt-agent-mode-463127"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                1M ChatGPT Agent Actions Study
              </a>{" "}
              by Jes Scholz
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
